/** @typedef {[number, number]} Point */

import { Group, Line, Rect } from 'react-konva';
import {
  buildCenterlineWallDimension,
  resolveWallSideFromRooms,
} from '../../../lib/floorPlanDesignerDimensions.js';
import FloorPlanDesignerDimension from '../../../components/FloorPlanDesignerDimension.jsx';

/**
 * @param {{
 *   anchor: Point;
 *   preview: Point;
 *   metersPerPixel: number;
 *   displayScale: number;
 *   stroke: string;
 *   strokeWidth: number;
 *   fill: string;
 * }} props
 */
function RectangularRoomPreview({
  anchor,
  preview,
  metersPerPixel,
  displayScale,
  stroke,
  strokeWidth,
  fill,
}) {
  const minX = Math.min(anchor[0], preview[0]);
  const maxX = Math.max(anchor[0], preview[0]);
  const minY = Math.min(anchor[1], preview[1]);
  const maxY = Math.max(anchor[1], preview[1]);
  const widthPx = maxX - minX;
  const heightPx = maxY - minY;

  if (widthPx <= 0 && heightPx <= 0) return null;

  const previewPolygon = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];

  const edges = [
    [minX, minY, maxX, minY],
    [maxX, minY, maxX, maxY],
    [maxX, maxY, minX, maxY],
    [minX, maxY, minX, minY],
  ];

  const widthDim =
    widthPx > 0
      ? buildCenterlineWallDimension([minX, minY], [maxX, minY], metersPerPixel, {
          roomPolygons: [previewPolygon],
          displayScale,
        })
      : null;
  const heightDim =
    heightPx > 0
      ? (() => {
          const dim = buildCenterlineWallDimension([minX, minY], [minX, maxY], metersPerPixel, {
            roomPolygons: [previewPolygon],
            displayScale,
          });
          if (!dim) return null;
          const sides = resolveWallSideFromRooms({
            start: [minX, minY],
            end: [minX, maxY],
            roomPolygons: [previewPolygon],
            displayScale,
          });
          return { ...dim, sideSign: sides.interiorSign, placementKind: 'interior' };
        })()
      : null;

  if (widthDim) {
    const sides = resolveWallSideFromRooms({
      start: [minX, minY],
      end: [maxX, minY],
      roomPolygons: [previewPolygon],
      displayScale,
    });
    widthDim.sideSign = sides.interiorSign;
    widthDim.placementKind = 'interior';
  }

  return (
    <Group listening={false}>
      <Rect
        x={minX}
        y={minY}
        width={widthPx}
        height={heightPx}
        stroke={stroke}
        strokeWidth={strokeWidth}
        fill={fill}
        opacity={0.12}
      />
      {edges.map((points, index) => (
        <Line
          key={`room-preview-edge-${index}`}
          points={points}
          stroke={stroke}
          strokeWidth={strokeWidth}
          opacity={0.85}
        />
      ))}
      {widthDim ? (
        <FloorPlanDesignerDimension
          start={widthDim.start}
          end={widthDim.end}
          lengthPx={widthDim.lengthPx}
          metersPerPixel={metersPerPixel}
          displayScale={displayScale}
          sideSign={widthDim.sideSign}
          text={widthDim.text}
          fontSize={12}
        />
      ) : null}
      {heightDim ? (
        <FloorPlanDesignerDimension
          start={heightDim.start}
          end={heightDim.end}
          lengthPx={heightDim.lengthPx}
          metersPerPixel={metersPerPixel}
          displayScale={displayScale}
          sideSign={heightDim.sideSign}
          text={heightDim.text}
          fontSize={12}
        />
      ) : null}
    </Group>
  );
}

export default RectangularRoomPreview;
